import { X } from "lucide-react";
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "~/components/ui/dialog";
import { useFontStyle } from "~/hook/useFontStyle";
import useLocale from "~/hook/useLocale";
import { getSession, setSession } from "~/lib/helperFuncation/sessionStorageService";
import { setIsSupperHeader } from "~/lib/store/slice/headerSlice";
import { useAppDispatch } from "~/lib/store/store";
import { cn } from "~/lib/utils";
import { SHOW_SUPER_HEADER } from "~/utils/constant";

interface SuperHeaderData {
  id: number;
  text: string;
  background_color: string | null;
  navigate_to: string | null;
  has_button: 0 | 1 | null;
  button_text: string | null;
  button_color: string | null;
  is_modal: 0 | 1 | null;
  model_content: string | null;
}

interface SuperHeaderProps {
  superHeader: SuperHeaderData;
}

function SuperHeader({ superHeader }: SuperHeaderProps) {
  const { normalInter, medium } = useFontStyle();
  const { LocalizedLink } = useLocale();
  const [showSuperHeader, setShowSuperHeader] = useState<boolean>(false);
  const dispatch = useAppDispatch();

  function getContrastingTextColor(hexColor: string): string {
    if (!hexColor) return "#000000";
    const hex = hexColor?.replace("#", "");
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 128 ? "#000000" : "#ffffff";
  }
  const textColor = getContrastingTextColor(superHeader?.background_color || "");
  const buttonTextColor = getContrastingTextColor(superHeader?.button_color ? superHeader?.button_color : "");

  const handleCloseButtonClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowSuperHeader(false);
    dispatch(setIsSupperHeader(false));
    setSession(SHOW_SUPER_HEADER, JSON.stringify(false));
  };

  useEffect(() => {
    const sessionValue = getSession(SHOW_SUPER_HEADER);
    if (sessionValue) {
      setShowSuperHeader(JSON.parse(sessionValue));
      dispatch(setIsSupperHeader(JSON.parse(sessionValue)));
    } else {
      setShowSuperHeader(true);
      setSession(SHOW_SUPER_HEADER, JSON.stringify(true));
      dispatch(setIsSupperHeader(true));
    }
  }, []);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (superHeader?.is_modal === 1 && showSuperHeader) {
      document.documentElement.style.overflowY = "hidden";
      return () => {
        document.documentElement.style.overflowY = "";
      };
    }
  }, [showSuperHeader, superHeader?.is_modal]);

  return (
    <>
      {superHeader?.is_modal === 1 ? (
        <>
          {/* Backdrop with blur */}
          {showSuperHeader && <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[98]" onClick={(e) => handleCloseButtonClick(e)} />}
          {/* Modal Dialog */}
          <Dialog
            modal={false}
            open={showSuperHeader}
            onOpenChange={(open) => {
              if (!open) {
                handleCloseButtonClick({ preventDefault: () => {}, stopPropagation: () => {} });
              }
            }}
          >
            <DialogContent
              isCloseIcon={false}
              className="w-[calc(100%-2rem)] max-w-xl z-[99] border-0 max-h-[80dvh] flex flex-col p-0 gap-0 rounded-3xl overflow-hidden shadow-2xl"
            >
              <DialogHeader className={cn("bg-secondaryColor px-4 py-4 shrink-0 border-b border-softApricot", normalInter)}>
                <div className="flex items-start justify-between gap-4">
                  <DialogTitle className={cn("text-textColorPrimary text-lg  overflow-hidden whitespace-nowrap truncate", normalInter)}>
                    {superHeader?.text}
                  </DialogTitle>
                  <button
                    onClick={(e) => handleCloseButtonClick(e)}
                    className="shrink-0 rounded-full p-1.5 border border-primaryColor hover:bg-softApricot text-titlePreviewColor/50 hover:text-titlePreviewColor transition-colors"
                    aria-label="Close"
                  >
                    <X className="size-4 text-primaryColor" />
                  </button>
                </div>
              </DialogHeader>

              {/* Content */}
              <div
                className={cn(
                  "text-textSecondaryColor leading-relaxed text-base space-y-4 overflow-y-auto overflow-x-hidden overscroll-contain px-6 py-5 bg-white custom-scrollbar",
                  normalInter,
                )}
                dangerouslySetInnerHTML={{ __html: superHeader?.model_content || "" }}
              />
            </DialogContent>
          </Dialog>
        </>
      ) : (
        <LocalizedLink
          to={superHeader?.navigate_to || "/"}
          key={superHeader?.id}
          onClick={(e) => {
            // e.preventDefault();
            // e.stopPropagation();
            setShowSuperHeader(false);
            setSession(SHOW_SUPER_HEADER, JSON.stringify(false));
            dispatch(setIsSupperHeader(false));
          }}
          className={cn(
            "w-full h-full flex gap-4 justify-center items-center superHeaders-center border-b-[1px] border-b-borderPrimary cursor-pointer p-2 relative",
            showSuperHeader || superHeader?.is_modal === 0 ? "flex" : "hidden",
          )}
          style={{
            backgroundColor: superHeader?.background_color || "",
          }}
        >
          <span className="text-14 font-abcArizonaFlareVariable font-normal" style={{ color: textColor }}>
            {superHeader?.text}
          </span>
          {superHeader?.has_button === 1 && (
            <button
              className="w-auto flex justify-center items-center p-1 px-4 capitalize rounded-full font-abcArizonaFlareVariable font-normal text-12"
              style={{
                backgroundColor: superHeader?.button_color || "white",
                color: buttonTextColor,
              }}
            >
              {superHeader?.button_text}
            </button>
          )}
          <X
            className="size-5 font-abcArizonaFlareVariable font-normal absolute right-5"
            onClick={(e) => handleCloseButtonClick(e)}
            style={{ color: textColor }}
          />
        </LocalizedLink>
      )}
    </>
  );
}

export default SuperHeader;
